// =============================================================
// קובץ: src/services/backoffice/articlesService.ts
// =============================================================

import { handleSessionError } from './authService';
import { API_BASE_URL } from '../../config/api';

// ============== Interfaces ==============

export interface Article {
    id: number;
    title: string;
    slug: string;
    excerpt?: string;
    content?: string;
    category_id?: number;
    category_name?: string;
    meta_description?: string;
    cover_image_url?: string | null;
    read_time_minutes: number;
    is_published: number;
    published_at?: string;
    ts: string;
}

export interface ArticleCategory {
    id: number;
    name: string;
    slug: string;
    icon?: string;
    is_active: number;
    sort_order: number;
    articles_count?: number;
}

export interface ArticlesListResponse {
    articles: Article[];
    total: number;
    page: number;
    limit: number;
}

// ============== Helper Functions ==============

const createFormData = (data: Record<string, any>): FormData => {
    const formData = new FormData();

    const sessionToken = localStorage.getItem('backoffice_session_id');
    if (sessionToken) {
        formData.append('session_token', sessionToken);
    }

    Object.entries(data).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            formData.append(key, value.toString());
        }
    });
    return formData;
};

const handleApiResponse = async (response: Response): Promise<any> => {
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();

    if (handleSessionError(result)) {
        throw new Error('Session expired - redirecting to login');
    }

    return result;
};

// ============== Service Class ==============

class ArticlesService {
    /**
     * קבלת רשימת מאמרים (כולל טיוטות)
     * תומך גם ב-id לטעינת מאמר בודד
     */
    async getArticles(params?: {
        id?: string | number;
        category_id?: number;
        page?: number;
        limit?: number;
    }): Promise<ArticlesListResponse> {
        try {
            const formData = createFormData({
                ...(params?.id !== undefined && { id: params.id }),
                ...(params?.category_id !== undefined && { category_id: params.category_id }),
                page: params?.page ?? 1,
                limit: params?.limit ?? 20,
            });

            const response = await fetch(
                `${API_BASE_URL}/Api/service/backoffice/get_articles`,
                { method: 'POST', body: formData }
            );

            const result = await handleApiResponse(response);

            if (result.res === 'NOK') {
                throw new Error(result.msg || 'Failed to fetch articles');
            }

            return {
                articles: result.articles || [],
                total: result.total || 0,
                page: result.page || 1,
                limit: result.limit || 20,
            };
        } catch (error) {
            console.error('Articles getArticles error:', error);
            throw error;
        }
    }

    /**
     * יצירת מאמר חדש — מקבל strings (כמו שהקומפוננטה שולחת)
     */
    async createArticle(data: {
        title: string;
        content: string;
        category_id?: string | number;
        excerpt?: string;
        meta_description?: string;
        cover_image_url?: string;
        read_time_minutes?: string | number;
        is_published?: string | number;
    }): Promise<Article> {
        try {
            const formData = createFormData(data);

            const response = await fetch(
                `${API_BASE_URL}/Api/service/backoffice/create_article`,
                { method: 'POST', body: formData }
            );

            const result = await handleApiResponse(response);

            if (result.res === 'NOK') {
                throw new Error(result.msg || 'Failed to create article');
            }

            return result.data;
        } catch (error) {
            console.error('Articles createArticle error:', error);
            throw error;
        }
    }

    /**
     * עדכון מאמר קיים — id יכול להגיע כstring מ-useParams
     */
    async updateArticle(data: {
        id?: string | number;
        title?: string;
        content?: string;
        category_id?: string | number;
        excerpt?: string;
        meta_description?: string;
        cover_image_url?: string;
        read_time_minutes?: string | number;
        is_published?: string | number;
    }): Promise<Article> {
        try {
            const formData = createFormData(data);

            const response = await fetch(
                `${API_BASE_URL}/Api/service/backoffice/update_article`,
                { method: 'POST', body: formData }
            );

            const result = await handleApiResponse(response);

            if (result.res === 'NOK') {
                throw new Error(result.msg || 'Failed to update article');
            }

            return result.data;
        } catch (error) {
            console.error('Articles updateArticle error:', error);
            throw error;
        }
    }

    /**
     * העלאת תמונה למאמר — מחזיר נתיב יחסי כמו /uploads/articles/2026/08/xxx.webp
     * הקובץ אמור להגיע כבר מוקטן (ראה utils/imageResize.ts); התקרה בשרת
     * היא רשת ביטחון בלבד.
     */
    async uploadArticleImage(file: File): Promise<string> {
        try {
            const formData = new FormData();
            const sessionToken = localStorage.getItem('backoffice_session_id');
            if (sessionToken) {
                formData.append('session_token', sessionToken);
            }
            formData.append('file', file);

            const response = await fetch(
                `${API_BASE_URL}/Api/service/backoffice/upload_article_image`,
                { method: 'POST', body: formData }
            );

            const result = await handleApiResponse(response);

            if (result.res === 'NOK' || !result.url) {
                throw new Error(result.msg || 'Failed to upload image');
            }

            return result.url as string;
        } catch (error) {
            console.error('Articles uploadArticleImage error:', error);
            throw error;
        }
    }

    /**
     * מחיקת מאמר
     */
    async deleteArticle(id: number): Promise<{ deleted: boolean }> {
        try {
            const formData = createFormData({ id });

            const response = await fetch(
                `${API_BASE_URL}/Api/service/backoffice/delete_article`,
                { method: 'POST', body: formData }
            );

            const result = await handleApiResponse(response);

            if (result.res === 'NOK') {
                throw new Error(result.msg || 'Failed to delete article');
            }

            return result.data;
        } catch (error) {
            console.error('Articles deleteArticle error:', error);
            throw error;
        }
    }

    /**
     * קבלת רשימת קטגוריות — מחזיר ArticleCategory[] ישירות
     */
    async getCategories(): Promise<ArticleCategory[]> {
        try {
            const formData = createFormData({});

            const response = await fetch(
                `${API_BASE_URL}/Api/service/backoffice/get_categories`,
                { method: 'POST', body: formData }
            );

            const result = await handleApiResponse(response);

            if (result.res === 'NOK') {
                throw new Error(result.msg || 'Failed to fetch categories');
            }

            // מחזיר את המערך ישירות — הקומפוננטות מצפות ל-ArticleCategory[]
            return result.categories || result.data?.categories || result.data || [];
        } catch (error) {
            console.error('Articles getCategories error:', error);
            throw error;
        }
    }

    /**
     * יצירת קטגוריה — מקבל strings
     */
    async createCategory(data: {
        name: string;
        icon?: string;
        sort_order?: string | number;
        is_active?: string | number;
    }): Promise<ArticleCategory> {
        try {
            const formData = createFormData(data);

            const response = await fetch(
                `${API_BASE_URL}/Api/service/backoffice/create_category`,
                { method: 'POST', body: formData }
            );

            const result = await handleApiResponse(response);

            if (result.res === 'NOK') {
                throw new Error(result.msg || 'Failed to create category');
            }

            return result.data;
        } catch (error) {
            console.error('Articles createCategory error:', error);
            throw error;
        }
    }

    /**
     * עדכון קטגוריה — id יכול להגיע כstring
     */
    async updateCategory(data: {
        id?: string | number;
        name?: string;
        icon?: string;
        is_active?: string | number;
        sort_order?: string | number;
    }): Promise<ArticleCategory> {
        try {
            const formData = createFormData(data);

            const response = await fetch(
                `${API_BASE_URL}/Api/service/backoffice/update_category`,
                { method: 'POST', body: formData }
            );

            const result = await handleApiResponse(response);

            if (result.res === 'NOK') {
                throw new Error(result.msg || 'Failed to update category');
            }

            return result.data;
        } catch (error) {
            console.error('Articles updateCategory error:', error);
            throw error;
        }
    }
}

export const articlesService = new ArticlesService();